import { db } from './firebase';
import { Transaction, TransactionStatus, UsageLog, SystemStats } from '../types';

// BANK CONFIG
export const BANK_INFO = {
  BIN: '970416', // ACB BIN code
  BANK_NAME: 'ACB',
  ACCOUNT_NO: '4870577',
  ACCOUNT_NAME: 'TRUONG NHAN'
};

// Generate VietQR Link
export const generateQrUrl = (amount: number, content: string): string => {
  const template = 'compact2';
  const encodedContent = encodeURIComponent(content);
  return `https://img.vietqr.io/image/${BANK_INFO.BANK_NAME}-${BANK_INFO.ACCOUNT_NO}-${template}.png?amount=${amount}&addInfo=${encodedContent}`;
};

// Create topup request
export const createTopupRequest = async (userId: string, userEmail: string, credits: number, amount: number): Promise<Transaction> => {
  try {
    const transferContent = `SEOMASTER ${userId.substring(0, 5).toUpperCase()}`;
    const txRef = db.collection('transactions').doc();
    
    // Support 1 decimal place for credits (e.g. 5.5 credits)
    const roundedCredits = Math.round(credits * 10) / 10;

    const newTx: Transaction = {
        id: txRef.id,
        userId,
        userEmail: userEmail || 'unknown@user.com',
        credits: roundedCredits,
        amount: Math.round(amount),
        content: transferContent,
        status: 'waiting_payment',
        bankCode: 'ACB',
        createdAt: new Date().toISOString()
    };
    await txRef.set(newTx);
    return newTx;
  } catch (error: any) {
    console.error("Create Topup Error:", error);
    throw new Error(`Không thể tạo giao dịch: ${error.message}`);
  }
};

// Submit Proof of Payment
export const submitTransactionProof = async (txId: string, base64Image: string): Promise<void> => {
  try {
    await db.collection('transactions').doc(txId).update({
      proofImage: base64Image,
      status: 'pending'
    });
  } catch (error) {
    console.error("Submit Proof Error:", error);
    throw error;
  }
};

// Get User Transactions
export const getUserTransactions = async (userId: string): Promise<Transaction[]> => {
  try {
    const snapshot = await db.collection('transactions').where('userId', '==', userId).get();
    const txs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Transaction));
    return txs
        .filter(t => t.status !== 'waiting_payment')
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  } catch (error) {
    console.error("Get Tx Error:", error);
    return [];
  }
};

// Get User Usage Logs
export const getUserUsageLogs = async (userId: string): Promise<UsageLog[]> => {
  try {
    const snapshot = await db.collection('usage_logs').where('userId', '==', userId).get();
    const logs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as UsageLog));
    return logs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  } catch (error) {
    console.error("Get Usage Error:", error);
    return [];
  }
};

// Admin: Get All Usage Logs (Global Monitoring)
export const getAllUsageLogs = async (limitCount: number = 200): Promise<UsageLog[]> => {
    try {
        const snapshot = await db.collection('usage_logs')
            .orderBy('timestamp', 'desc')
            .limit(limitCount)
            .get();
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as UsageLog));
    } catch (error) {
        console.error("Get Global Usage Error:", error);
        // Fallback for missing index
        const snapshot = await db.collection('usage_logs').get();
        const logs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as UsageLog));
        return logs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).slice(0, limitCount);
    }
};

// Admin: Get All Pending Transactions
export const getPendingTransactions = async (): Promise<Transaction[]> => {
  try {
    const snapshot = await db.collection('transactions').where('status', '==', 'pending').get();
    const txs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Transaction));
    return txs.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  } catch (error) {
    console.error("Get Pending Tx Error:", error);
    return [];
  }
};

// Admin: Get All Transaction History
export const getAllTransactions = async (): Promise<Transaction[]> => {
  try {
    const snapshot = await db.collection('transactions').get();
    const txs = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as Transaction))
        .filter(t => t.status !== 'waiting_payment')
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 100); 
    return txs;
  } catch (error) {
    console.error("Get All Tx Error:", error);
    return [];
  }
};

// Admin: Get System Stats
export const getSystemStats = async (): Promise<SystemStats> => {
  try {
    const usersSnap = await db.collection('users').get();
    const totalUsers = usersSnap.size;
    const pendingSnap = await db.collection('transactions').where('status', '==', 'pending').get();
    const pendingTransactions = pendingSnap.size;
    const approvedSnap = await db.collection('transactions').where('status', '==', 'approved').get();
    const totalRevenue = approvedSnap.docs.reduce((acc, doc) => acc + (doc.data().amount || 0), 0);
    const logsSnap = await db.collection('usage_logs').get();
    const totalAiRequests = logsSnap.size;
    return { totalUsers, totalRevenue, pendingTransactions, totalAiRequests };
  } catch (error) {
    console.error("System Stats Error:", error);
    return { totalUsers: 0, totalRevenue: 0, pendingTransactions: 0, totalAiRequests: 0 };
  }
};

// Admin: Approve Transaction
export const approveTransaction = async (txId: string, adminId: string): Promise<void> => {
  try {
    await db.runTransaction(async (transaction) => {
      const txRef = db.collection('transactions').doc(txId);
      const txDoc = await transaction.get(txRef);
      if (!txDoc.exists) throw new Error("Giao dịch không tồn tại.");
      const txData = txDoc.data() as Transaction;
      if (txData.status !== 'pending') throw new Error("Giao dịch này đã được xử lý.");
      const userRef = db.collection('users').doc(txData.userId);
      const userDoc = await transaction.get(userRef);
      if (!userDoc.exists) throw new Error("User không tồn tại.");
      
      transaction.update(txRef, {
        status: 'approved',
        processedAt: new Date().toISOString(),
        processedBy: adminId
      });
      
      const userData = userDoc.data();
      // Round credits after addition (1 decimal)
      const newCredits = Math.round(((userData?.credits || 0) + txData.credits) * 10) / 10;
      
      transaction.update(userRef, {
        credits: newCredits,
        totalDeposited: (userData?.totalDeposited || 0) + txData.amount
      });
    });
  } catch (error) {
    console.error("Approve Tx Error:", error);
    throw error;
  }
};

// Admin: Reject Transaction
export const rejectTransaction = async (txId: string, adminId: string): Promise<void> => {
  try {
    const txRef = db.collection('transactions').doc(txId);
    await txRef.update({
      status: 'rejected',
      processedAt: new Date().toISOString(),
      processedBy: adminId
    });
  } catch (error) {
    console.error("Reject Tx Error:", error);
    throw error;
  }
};

// User: Deduct Credit
export const deductUserCredit = async (userId: string, cost: number = 1, action: string = 'Unknown', note: string = ''): Promise<boolean> => {
  try {
    let success = false;
    await db.runTransaction(async (transaction) => {
      const userRef = db.collection('users').doc(userId);
      const userDoc = await transaction.get(userRef);
      if (!userDoc.exists) throw new Error("User not found");
      const currentCredits = userDoc.data()?.credits || 0;
      
      if (currentCredits < cost) {
        success = false;
        return;
      }
      
      // FIX: Round to 1 decimal place
      const newBalance = Math.round((currentCredits - cost) * 10) / 10;
      
      transaction.update(userRef, { credits: newBalance });
      const logRef = db.collection('usage_logs').doc();
      
      transaction.set(logRef, {
        userId, 
        userEmail: userDoc.data()?.email || 'unknown@user.com',
        action, 
        note: note ? note.substring(0, 100) : '', 
        cost, 
        timestamp: new Date().toISOString()
      });
      success = true;
    });
    return success;
  } catch (error) {
    console.error("Deduct Credit Error:", error);
    return false;
  }
};

export const refundUserCredit = async (userId: string, amount: number = 1): Promise<void> => {
  try {
    await db.runTransaction(async (transaction) => {
      const userRef = db.collection('users').doc(userId);
      const userDoc = await transaction.get(userRef);
      if (!userDoc.exists) return;
      const currentCredits = userDoc.data()?.credits || 0;
      
      // FIX: Round to 1 decimal place
      const newBalance = Math.round((currentCredits + amount) * 10) / 10;
      
      transaction.update(userRef, { credits: newBalance });
      const logRef = db.collection('usage_logs').doc();
      transaction.set(logRef, {
        userId, 
        userEmail: userDoc.data()?.email || 'unknown@user.com',
        action: 'System Refund', 
        note: 'Hoàn tiền do lỗi hệ thống/API', 
        cost: -amount, 
        timestamp: new Date().toISOString()
      });
    });
  } catch (error) {
    console.error("Refund Credit Error:", error);
  }
};